// User dropdown — opens from the topbar avatar.
//
// Deliberately minimal: navigation + essential actions only. Everything
// configurable (2FA, device tokens, secrets, downloads, the show-tool-
// calls / debug-telemetry toggles) lives in Settings now — this menu is
// just the way IN to Settings/Admin plus sign-out.

import { useEffect, useRef } from "preact/hooks";
import type { ComponentChildren } from "preact";
import type { Me } from "../lib/use-me";
import { Icon, type IconName } from "../icons";

interface UserMenuProps {
  open: boolean;
  onClose: () => void;
  onOpenSettings: () => void;
  onOpenAdmin: () => void;
  onOpenFamily: () => void;
  onSignOut: () => void;
  me: Me;
}

export function UserMenu({
  open, onClose, onOpenSettings, onOpenAdmin, onOpenFamily, onSignOut, me,
}: UserMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, onClose]);
  if (!open) return null;

  return (
    <div className="user-menu" ref={ref} role="menu">
      <div className="um-head">
        <div className="avatar" style={{ width: 36, height: 36 }}>{me.initial}</div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: "var(--f-mono)",
              fontSize: 10.5,
              color: "var(--text-dim)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            Signed in as
          </div>
          <div style={{ fontSize: 13.5, fontWeight: 600 }}>
            {me.username}{" "}
            <span style={{ color: "var(--text-dim)", fontWeight: 400 }}>· {me.role}</span>
          </div>
        </div>
      </div>

      <UmGroup>
        <UmItem
          icon="settings"
          label="Settings"
          onClick={() => { onClose(); onOpenSettings(); }}
        />
        <UmItem
          icon="people"
          label="Family"
          onClick={() => { onClose(); onOpenFamily(); }}
        />
      </UmGroup>

      {me.role === "admin" && (
        <UmGroup>
          <UmItem
            icon="shield"
            label="Admin settings"
            tone="accent"
            onClick={() => { onClose(); onOpenAdmin(); }}
          />
          <UmItem icon="compact" label="Clear cache and reload" onClick={() => {
            try { localStorage.clear(); } catch {}
            location.reload();
          }} />
        </UmGroup>
      )}

      <UmGroup last>
        <UmItem
          icon="logout"
          label="Sign out"
          tone="danger"
          onClick={() => { onClose(); onSignOut(); }}
        />
      </UmGroup>
    </div>
  );
}

function UmGroup({ children, last }: { children: ComponentChildren; last?: boolean }) {
  return <div className="um-grp" data-last={!!last}>{children}</div>;
}

function UmItem(
  { icon, label, badge, tone, onClick }:
  { icon: IconName; label: string; badge?: string | null; tone?: "accent" | "danger"; onClick?: () => void }
) {
  return (
    <button className="um-item" data-tone={tone || ""} onClick={onClick}>
      <span className="um-ico"><Icon name={icon} size={15} /></span>
      <span className="um-lbl">{label}</span>
      {badge && <span className="um-badge">{badge}</span>}
    </button>
  );
}
